import { motion } from "framer-motion";
import React, { useCallback, useEffect, useState } from "react";
import { ReactComponent as CheckIcon } from "../../../../assets/icons/check.svg";
import {
  SystemPlanRequest,
  SystemPlanResponse,
} from "../../../../features/subscription/@types";
import { useGetSystemPlanMutation } from "../../../../features/subscription/subscriptionApiSlice";
import { formatCurrency } from "../../../../lib/utils";

interface PricingPlan {
  name: string;
  monthlyPrice: number;
  yearlyPrice: number;
  description: string;
  features: string[];
  buttonText: string;
}

const PricingComponent: React.FC = () => {
  const [isYearly, setIsYearly] = useState(false);

  // RTK Query mutation
  const [getPlanConfig, { data: maintenanceData, isLoading }] =
    useGetSystemPlanMutation();

  const fetchPlanConfig = useCallback(async () => {
    const requestBody: SystemPlanRequest = {
      pageNumber: 1,
      pageSize: 100,
    };

    try {
      await getPlanConfig(requestBody).unwrap();
    } catch (err) {
      console.error("Failed to fetch expense heads:", err);
    }
  }, [getPlanConfig]);

  // Fetch expense heads on component mount and when filters change
  useEffect(() => {
    fetchPlanConfig();
  }, [fetchPlanConfig]);

  const response = maintenanceData?.data as SystemPlanResponse | undefined;

  const plans: PricingPlan[] = [
    {
      name: "Basic",
      monthlyPrice: 10000,
      yearlyPrice: 119000,
      description:
        "Perfect for small businesses looking to track vehicles, monitor trips, and manage expenses with ease.",
      features: [
        "Manage up to 20 vehicles",
        "Real-time vehicle tracking",
        "Driver trip monitoring",
        "Profit & expense tracking",
      ],
      buttonText: "Get started",
    },
    {
      name: "Professional",
      monthlyPrice: 15000,
      yearlyPrice: 179000,
      description:
        "Ideal for growing businesses that need advanced tracking, analytics, and automation to scale efficiently.",
      features: [
        "Manage unlimited vehicles",
        "Advanced trip analytics & reports",
        "Fuel consumption monitoring",
        "Automated profit & expense insights",
        "Priority support",
      ],
      buttonText: "Get started",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  const priceVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: { duration: 0.3 },
    },
  };

  return (
    <div className="min-h-screen py-36 px-4 sm:px-6 lg:px-8">
      <motion.div
        className="max-w-7xl mx-auto"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-hColor mb-8">
            Choose the plan that works for you
          </h1>

          {/* Toggle Switch */}
          <div className="inline-flex rounded-full bg-gray-200 p-1">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsYearly(false)}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                !isYearly ? "bg-[#90AA36] text-white" : "text-gray-600"
              }`}
            >
              Monthly
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsYearly(true)}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                isYearly ? "bg-[#90AA36] text-white" : "text-gray-600"
              }`}
            >
              Yearly
            </motion.button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {response?.data
            ? response?.data?.map((plan) => (
                <motion.div
                  key={plan.name}
                  variants={cardVariants}
                  className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 relative overflow-hidden hover:shadow-xl transition-shadow duration-300"
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.2 }}
                >
                  {/* Plan Name */}
                  <div className="mb-6 bg-[#F6F6F6] rounded-md px-3 w-fit flex items-center justify-center">
                    <h3 className="text-xl font-semibold text-hColor mb-2">
                      {plan.name}
                    </h3>
                  </div>

                  {/* Price */}
                  <div
                    className="mb-6"
                    // variants={priceVariants}
                    key={isYearly ? "yearly" : "monthly"}
                  >
                    <div className="flex items-baseline">
                      <span className="text-4xl font-bold text-hColor">
                        {isYearly
                          ? formatCurrency(plan.yearlyPrice)
                          : formatCurrency(plan.monthlyPrice)}
                      </span>
                      <span className="text-lg text-gray-500 ml-1">
                        /{isYearly ? "year" : "month/per vehicle"}
                      </span>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-pColor mb-8 leading-relaxed">
                    {plan.description}
                  </p>

                  <div className="border-t border-gray-200"></div>

                  {/* Features */}
                  <div className="my-8">
                    <h4 className="text-lg font-semibold text-hColor mb-4">
                      What's included
                    </h4>
                    <ul className="space-y-3 h-[200px]">
                      {plan.featuresList.map((feature, featureIndex) => (
                        <motion.li
                          key={featureIndex}
                          className="flex items-start space-x-3"
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.1 * featureIndex }}
                        >
                          <div className="flex-shrink-0 mt-0.5">
                            <div className="w-5 h-5">
                              <CheckIcon className="w-full h-full text-primary" />
                            </div>
                          </div>
                          <span className="text-pColor leading-relaxed">
                            {feature}
                          </span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  {/* CTA Button */}
                  <motion.button
                    className="w-full bg-primary text-white py-3 px-6 rounded-lg font-medium hover:bg-primary/60 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Get Started
                  </motion.button>
                </motion.div>
              ))
            : plans.map((plan) => (
                <motion.div
                  key={plan.name}
                  variants={cardVariants}
                  className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 relative overflow-hidden hover:shadow-xl transition-shadow duration-300"
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.2 }}
                >
                  {/* Plan Name */}
                  <div className="mb-6 bg-[#F6F6F6] rounded-md px-3 w-fit flex items-center justify-center">
                    <h3 className="text-xl font-semibold text-hColor mb-2">
                      {plan.name}
                    </h3>
                  </div>

                  {/* Price */}
                  <div
                    className="mb-6"
                    // variants={priceVariants}
                    key={isYearly ? "yearly" : "monthly"}
                  >
                    <div className="flex items-baseline">
                      <span className="text-4xl font-bold text-hColor">
                        {isYearly
                          ? formatCurrency(plan.yearlyPrice)
                          : formatCurrency(plan.monthlyPrice)}
                      </span>
                      <span className="text-lg text-gray-500 ml-1">
                        /{isYearly ? "year" : "month/per vehicle"}
                      </span>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-pColor mb-8 leading-relaxed">
                    {plan.description}
                  </p>

                  <div className="border-t border-gray-200"></div>

                  {/* Features */}
                  <div className="my-8">
                    <h4 className="text-lg font-semibold text-hColor mb-4">
                      What's included
                    </h4>
                    <ul className="space-y-3 h-[200px]">
                      {plan.features.map((feature, featureIndex) => (
                        <motion.li
                          key={featureIndex}
                          className="flex items-start space-x-3"
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.1 * featureIndex }}
                        >
                          <div className="flex-shrink-0 mt-0.5">
                            <div className="w-5 h-5">
                              <CheckIcon className="w-full h-full text-primary" />
                            </div>
                          </div>
                          <span className="text-pColor leading-relaxed">
                            {feature}
                          </span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  {/* CTA Button */}
                  <motion.button
                    className="w-full bg-primary text-white py-3 px-6 rounded-lg font-medium hover:bg-primary/60 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {plan.buttonText}
                  </motion.button>
                </motion.div>
              ))}
        </div>

        {/* Save Badge for Yearly */}
        {isYearly && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center mt-6"
          >
            <div className="inline-flex items-center px-4 py-2 bg-green-100 text-primary rounded-full text-sm font-medium">
              💰 Save up to 17% with yearly billing
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default PricingComponent;
